import type {
  AnimationEasing,
  AnimationInterpolation,
  AnimationLoopMode,
  GSplatParticleMotion,
  GSplatParticleSizeMode,
} from '@sharefe/share3d-engine'
import { GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS } from '@/plugins/gsplat/effects/GSplatChaoticParticleScatterEffect'
import { GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS } from '@/plugins/gsplat/effects/gsplatEffectTemplate'
import {
  GSPLAT_LAYERED_PRINT_DIRECTIONS,
  GSPLAT_PARTICLE_MOTION_DEFAULTS,
  GSPLAT_PARTICLE_MOTION_TYPES,
} from '@/plugins/gsplat/effects/particleMotion'

export const GS3D_EFFECT_ANIMATION_PRESET_KIND = 'share3d.example.gs3d-effect-animation'
export const GS3D_EFFECT_ANIMATION_PRESET_VERSION = 1

export const GS3D_EFFECT_ANIMATION_PRESET_INTERPOLATIONS = [
  'linear',
  'spline',
  'step',
] as const satisfies readonly AnimationInterpolation[]

export const GS3D_EFFECT_ANIMATION_PRESET_EASINGS = [
  'none',
  'linear',
  'linearIn',
  'linearOut',
  'linearInOut',
  'quadraticIn',
  'quadraticOut',
  'quadraticInOut',
  'cubicIn',
  'cubicOut',
  'cubicInOut',
  'quarticIn',
  'quarticOut',
  'quarticInOut',
  'quinticIn',
  'quinticOut',
  'quinticInOut',
  'sineIn',
  'sineOut',
  'sineInOut',
  'expoIn',
  'expoOut',
  'expoInOut',
  'circIn',
  'circOut',
  'circInOut',
  'elasticIn',
  'elasticOut',
  'elasticInOut',
  'backIn',
  'backOut',
  'backInOut',
  'bounceIn',
  'bounceOut',
  'bounceInOut',
] as const satisfies readonly ('none' | AnimationEasing)[]

export const GS3D_EFFECT_ANIMATION_PRESET_LOOP_MODES = [
  'once',
  'repeat',
  'pingpong',
] as const satisfies readonly AnimationLoopMode[]

export const GS3D_EFFECT_ANIMATION_PRESET_BOX_DIRECTIONS = [
  'z-up',
  'z-down',
  'x-plus',
  'x-minus',
  'y-plus',
  'y-minus',
] as const

export const GS3D_EFFECT_ANIMATION_PRESET_EFFECT_KEYS = [
  'box-reveal',
  'box-hide',
  'box-tint',
  'box-untint',
  'box-crop',
  'reveal-radial',
  'reveal-rain',
  'reveal-grid',
  'obb-double-wave',
  'visual-electronic',
  'visual-deep-meditation',
  'visual-waves',
  'visual-flare',
  'visual-disintegrate',
  'reveal-magic',
  'reveal-spread',
  'reveal-unroll',
  'reveal-twister',
  'particle-scatter-reassemble',
  'dissolve-drift',
] as const

export const GS3D_EFFECT_ANIMATION_PRESET_VISUAL_SHOWCASE_KEYS = [
  'visual-electronic',
  'visual-deep-meditation',
  'visual-waves',
  'visual-flare',
  'visual-disintegrate',
] as const

export const GS3D_EFFECT_ANIMATION_PRESET_REVEAL_SHOWCASE_KEYS = [
  'reveal-magic',
  'reveal-spread',
  'reveal-unroll',
  'reveal-twister',
] as const

export const GS3D_EFFECT_ANIMATION_PRESET_DISSOLVE_KEYS = ['dissolve-drift'] as const
export const GS3D_EFFECT_ANIMATION_PRESET_PARTICLE_KEYS = [
  'particle-scatter-reassemble',
] as const
export const GS3D_EFFECT_ANIMATION_PARTICLE_SIZE_MODES = [
  'fixed',
  'adaptive',
] as const satisfies readonly GSplatParticleSizeMode[]

export const GS3D_EFFECT_ANIMATION_PRESET_DEFAULTS = {
  currentFov: 65,
  boxSize: 4,
  boxDuration: 3.5,
} as const

export const GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS = {
  motion: {
    type: 'chaotic',
    params: { ...GSPLAT_PARTICLE_MOTION_DEFAULTS.chaotic },
  },
  style: {
    sizeMode: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleSizeMode,
    targetSize: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleTargetSize,
    targetSizeRatio: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleTargetSizeRatio,
    opacity: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleOpacity,
    highlightStrength: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleHighlightStrength,
    glowStrength: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleGlowStrength,
    glowRadius: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleGlowRadius,
  },
  jitter: {
    amplitude: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleJitterAmplitude,
    speed: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.particleJitterSpeed,
  },
  timing: {
    duration: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.duration,
    preScatterHoldDuration:
      GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.preScatterHoldDuration,
    peakHoldDuration: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.peakHoldDuration,
    phaseWeights: { ...GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.phaseWeights },
  },
  random: {
    seed: GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS.seed,
  },
} as const

export const GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS = {
  cameraFov: { min: 20, max: 100 },
  boxSize: { min: 0.5, max: 1000 },
  boxDuration: { min: 0.5, max: 12 },
  colorIntensity: { min: 0, max: 5 },
  revealDuration: { min: 1.2, max: 20 },
  radial: {
    intensity: { min: 0, max: 3 },
    speed: { min: 0, max: 20 },
    acceleration: { min: 0, max: 10 },
    delay: { min: 0, max: 10 },
    oscillationIntensity: { min: 0, max: 1 },
    bandWidth: { min: 0.1, max: 5 },
  },
  rain: {
    fallTintIntensity: { min: 0, max: 1 },
    intensity: { min: 0, max: 3 },
    distance: { min: 0, max: 120 },
    speed: { min: 0, max: 20 },
    acceleration: { min: 0, max: 10 },
    flightTime: { min: 0.1, max: 8 },
    rainSize: { min: 0.001, max: 0.2 },
    rotation: { min: 0, max: 2 },
    hitDuration: { min: 0.1, max: 4 },
  },
  grid: {
    moveTintIntensity: { min: 0, max: 1 },
    intensity: { min: 0, max: 3 },
    blockSize: { min: 0.1, max: 10 },
    delay: { min: 0, max: 2 },
    duration: { min: 0.1, max: 5 },
    dotSize: { min: 0.001, max: 0.2 },
    landDuration: { min: 0.1, max: 4 },
  },
  obb: {
    intensity: { min: 0, max: 3 },
    speed: { min: 0, max: 20 },
    acceleration: { min: 0, max: 10 },
    delay: { min: 0, max: 10 },
    oscillationIntensity: { min: 0, max: 1 },
    bandWidth: { min: 0.1, max: 5 },
  },
  visualShowcase: {
    intensity: { min: 0, max: 3 },
    timeScale: { min: 0, max: 4 },
    swayAmplitude: { min: 0, max: 1 },
    swaySpeed: { min: 0, max: 8 },
  },
  revealShowcase: {
    timeScale: { min: 0, max: 4 },
    motionScale: { min: 0, max: 5 },
    duration: { min: 0.5, max: 20 },
  },
  particleScatter: {
    duration: { min: 0.1, max: 60 },
    scatterScale: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.scatterScale,
    layerCount: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.layerCount,
    printBandWidth: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.printBandWidth,
    liftScale: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.liftScale,
    orbitScale: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.orbitScale,
    orbitFrequency: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.orbitFrequency,
    orbitSweepWidth: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.orbitSweepWidth,
    particleTargetSize: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleTargetSize,
    particleTargetSizeRatio: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleTargetSizeRatio,
    particleOpacity: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleOpacity,
    particleHighlightStrength:
      GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleHighlightStrength,
    particleGlowStrength: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleGlowStrength,
    particleGlowRadius: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleGlowRadius,
    particleJitterAmplitude: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleJitterAmplitude,
    particleJitterSpeed: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.particleJitterSpeed,
    preScatterHoldDuration: { min: 0, max: 60 },
    peakHoldDuration: { min: 0, max: 60 },
    seed: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.seed,
    phaseWeight: GSPLAT_PARTICLE_SCATTER_TEMPLATE_LIMITS.phaseWeight,
  },
  dissolve: {
    speed: { min: 0.05, max: 5 },
    motionScale: { min: 0, max: 5 },
  },
} as const

type CameraEasingMode = (typeof GS3D_EFFECT_ANIMATION_PRESET_EASINGS)[number]

export type GS3DEffectAnimationPresetBoxDirection =
  (typeof GS3D_EFFECT_ANIMATION_PRESET_BOX_DIRECTIONS)[number]

export type GS3DEffectAnimationPresetEffectKey =
  (typeof GS3D_EFFECT_ANIMATION_PRESET_EFFECT_KEYS)[number]
export type GS3DEffectAnimationPresetVisualShowcaseKey =
  (typeof GS3D_EFFECT_ANIMATION_PRESET_VISUAL_SHOWCASE_KEYS)[number]
export type GS3DEffectAnimationPresetRevealShowcaseKey =
  (typeof GS3D_EFFECT_ANIMATION_PRESET_REVEAL_SHOWCASE_KEYS)[number]
export type GS3DEffectAnimationPresetDissolveKey =
  (typeof GS3D_EFFECT_ANIMATION_PRESET_DISSOLVE_KEYS)[number]
export type GS3DEffectAnimationPresetParticleKey =
  (typeof GS3D_EFFECT_ANIMATION_PRESET_PARTICLE_KEYS)[number]

export type GS3DEffectAnimationPresetVector3 = [number, number, number]
export type GS3DEffectAnimationPresetColorTuple = [number, number, number]

export interface GS3DEffectAnimationPresetColorControl {
  color: string
  intensity: number
}

export interface GS3DEffectAnimationPresetColorControlInput {
  hex: string
  intensity: number
}

export interface GS3DEffectAnimationPresetCameraKeyframe {
  time: number
  position: GS3DEffectAnimationPresetVector3
  target: GS3DEffectAnimationPresetVector3
  fov: number
}

export interface GS3DEffectAnimationPresetCamera {
  interpolation: AnimationInterpolation
  easing: CameraEasingMode
  loopMode: AnimationLoopMode
  currentFov: number
  keyframes: GS3DEffectAnimationPresetCameraKeyframe[]
}

export interface GS3DEffectAnimationPresetBox {
  size: number
  direction: GS3DEffectAnimationPresetBoxDirection
  duration: number
  tintColor: string
  edgeColor: string
}

export interface GS3DEffectAnimationPresetRadialReveal {
  duration: number
  dotTint: GS3DEffectAnimationPresetColorControl
  waveTint: GS3DEffectAnimationPresetColorControl
  intensity: number
}

export interface GS3DEffectAnimationPresetRadialRevealManual {
  speed: number
  acceleration: number
  delay: number
  dotTint: GS3DEffectAnimationPresetColorControl
  waveTint: GS3DEffectAnimationPresetColorControl
  oscillationIntensity: number
  bandWidth: number
}

export interface GS3DEffectAnimationPresetRainReveal {
  duration: number
  fallTint: GS3DEffectAnimationPresetColorControl
  fallTintIntensity: number
  hitTint: GS3DEffectAnimationPresetColorControl
  intensity: number
}

export interface GS3DEffectAnimationPresetRainRevealManual {
  distance: number
  speed: number
  acceleration: number
  flightTime: number
  rainSize: number
  rotation: number
  fallTint: GS3DEffectAnimationPresetColorControl
  fallTintIntensity: number
  hitTint: GS3DEffectAnimationPresetColorControl
  hitDuration: number
}

export interface GS3DEffectAnimationPresetGridReveal {
  duration: number
  moveTint: GS3DEffectAnimationPresetColorControl
  moveTintIntensity: number
  landTint: GS3DEffectAnimationPresetColorControl
  intensity: number
}

export interface GS3DEffectAnimationPresetGridRevealManual {
  blockSize: number
  delay: number
  duration: number
  dotSize: number
  moveTint: GS3DEffectAnimationPresetColorControl
  moveTintIntensity: number
  landTint: GS3DEffectAnimationPresetColorControl
  landDuration: number
}

export type GS3DEffectAnimationPresetObbReveal = GS3DEffectAnimationPresetRadialReveal
export type GS3DEffectAnimationPresetObbRevealManual =
  GS3DEffectAnimationPresetRadialRevealManual

export interface GS3DEffectAnimationPresetVisualShowcase {
  intensity: number
  timeScale: number
  swayAmplitude?: number
  swaySpeed?: number
  flickerColor?: GS3DEffectAnimationPresetColorControl
}

export interface GS3DEffectAnimationPresetVisualShowcaseState {
  intensity: number
  timeScale: number
  swayAmplitude?: number
  swaySpeed?: number
  flickerColor?: GS3DEffectAnimationPresetColorControlInput
}

export interface GS3DEffectAnimationPresetRevealShowcase {
  timeScale: number
  motionScale: number
  duration: number
}

export interface GS3DEffectAnimationPresetDissolveDrift {
  speed: number
  motionScale: number
}

export interface GS3DEffectAnimationPresetParticleScatter {
  motion: GSplatParticleMotion
  style: {
    sizeMode: GSplatParticleSizeMode
    targetSize: number
    targetSizeRatio: number
    opacity: number
    highlightStrength: number
    glowStrength: number
    glowRadius: number
  }
  jitter: {
    amplitude: number
    speed: number
  }
  timing: {
    duration: number
    preScatterHoldDuration: number
    peakHoldDuration: number
    phaseWeights: {
      particleize: number
      scatter: number
      gather: number
      restore: number
    }
  }
  random: {
    seed: number
  }
}

export type GS3DEffectAnimationPresetBoxEffectKey =
  | 'box-reveal'
  | 'box-hide'
  | 'box-tint'
  | 'box-untint'
  | 'box-crop'

export type GS3DEffectAnimationPresetEffect = (
  | {
    selectedEffectKey: GS3DEffectAnimationPresetBoxEffectKey
    params: GS3DEffectAnimationPresetBox
  }
  | {
    selectedEffectKey: 'reveal-radial'
    adaptive?: true
    params: GS3DEffectAnimationPresetRadialReveal
  }
  | {
    selectedEffectKey: 'reveal-radial'
    adaptive: false
    params: GS3DEffectAnimationPresetRadialRevealManual
  }
  | {
    selectedEffectKey: 'reveal-rain'
    adaptive?: true
    params: GS3DEffectAnimationPresetRainReveal
  }
  | {
    selectedEffectKey: 'reveal-rain'
    adaptive: false
    params: GS3DEffectAnimationPresetRainRevealManual
  }
  | {
    selectedEffectKey: 'reveal-grid'
    adaptive?: true
    params: GS3DEffectAnimationPresetGridReveal
  }
  | {
    selectedEffectKey: 'reveal-grid'
    adaptive: false
    params: GS3DEffectAnimationPresetGridRevealManual
  }
  | {
    selectedEffectKey: 'obb-double-wave'
    adaptive?: true
    params: GS3DEffectAnimationPresetObbReveal
  }
  | {
    selectedEffectKey: 'obb-double-wave'
    adaptive: false
    params: GS3DEffectAnimationPresetObbRevealManual
  }
  | {
    selectedEffectKey: GS3DEffectAnimationPresetVisualShowcaseKey
    params: GS3DEffectAnimationPresetVisualShowcase
  }
  | {
    selectedEffectKey: GS3DEffectAnimationPresetRevealShowcaseKey
    params: GS3DEffectAnimationPresetRevealShowcase
  }
  | {
    selectedEffectKey: GS3DEffectAnimationPresetParticleKey
    params: GS3DEffectAnimationPresetParticleScatter
  }
  | {
    selectedEffectKey: 'dissolve-drift'
    params: GS3DEffectAnimationPresetDissolveDrift
  }
)

export interface GS3DEffectAnimationPresetV1 {
  kind: typeof GS3D_EFFECT_ANIMATION_PRESET_KIND
  version: typeof GS3D_EFFECT_ANIMATION_PRESET_VERSION
  camera: GS3DEffectAnimationPresetCamera
  effect: GS3DEffectAnimationPresetEffect
}

export interface GS3DEffectAnimationPresetState {
  camera: GS3DEffectAnimationPresetCamera
  effect: {
    selectedEffectKey: string
    adaptive?: boolean
    box: {
      size: number
      direction: string
      duration: number
      tintColor: string
      edgeColor: string
    }
    reveal: {
      radial: Omit<GS3DEffectAnimationPresetRadialReveal, 'dotTint' | 'waveTint'> & {
        dotTint: GS3DEffectAnimationPresetColorControlInput
        waveTint: GS3DEffectAnimationPresetColorControlInput
      }
      rain: Omit<GS3DEffectAnimationPresetRainReveal, 'fallTint' | 'hitTint'> & {
        fallTint: GS3DEffectAnimationPresetColorControlInput
        hitTint: GS3DEffectAnimationPresetColorControlInput
      }
      grid: Omit<GS3DEffectAnimationPresetGridReveal, 'moveTint' | 'landTint'> & {
        moveTint: GS3DEffectAnimationPresetColorControlInput
        landTint: GS3DEffectAnimationPresetColorControlInput
      }
      obb: Omit<GS3DEffectAnimationPresetObbReveal, 'dotTint' | 'waveTint'> & {
        dotTint: GS3DEffectAnimationPresetColorControlInput
        waveTint: GS3DEffectAnimationPresetColorControlInput
      }
    }
    revealManual: {
      radial: Omit<GS3DEffectAnimationPresetRadialRevealManual, 'dotTint' | 'waveTint'> & {
        dotTint: GS3DEffectAnimationPresetColorControlInput
        waveTint: GS3DEffectAnimationPresetColorControlInput
      }
      rain: Omit<GS3DEffectAnimationPresetRainRevealManual, 'fallTint' | 'hitTint'> & {
        fallTint: GS3DEffectAnimationPresetColorControlInput
        hitTint: GS3DEffectAnimationPresetColorControlInput
      }
      grid: Omit<GS3DEffectAnimationPresetGridRevealManual, 'moveTint' | 'landTint'> & {
        moveTint: GS3DEffectAnimationPresetColorControlInput
        landTint: GS3DEffectAnimationPresetColorControlInput
      }
      obb: Omit<GS3DEffectAnimationPresetObbRevealManual, 'dotTint' | 'waveTint'> & {
        dotTint: GS3DEffectAnimationPresetColorControlInput
        waveTint: GS3DEffectAnimationPresetColorControlInput
      }
    }
    visual: Record<GS3DEffectAnimationPresetVisualShowcaseKey, GS3DEffectAnimationPresetVisualShowcaseState>
    showcaseReveal: Record<
      GS3DEffectAnimationPresetRevealShowcaseKey,
      GS3DEffectAnimationPresetRevealShowcase
    >
    particle: Record<
      GS3DEffectAnimationPresetParticleKey,
      GS3DEffectAnimationPresetParticleScatter
    >
    dissolve: Record<GS3DEffectAnimationPresetDissolveKey, GS3DEffectAnimationPresetDissolveDrift>
  }
}

export interface GS3DEffectAnimationPresetApplyOperations {
  stopPlayback: () => void
  clearActiveEffect: () => void
  writeState: (preset: GS3DEffectAnimationPresetV1) => void
  applyFirstKeyframe: (frame: GS3DEffectAnimationPresetCameraKeyframe) => void
  applyCurrentFov: (fov: number) => void
}

type NumericLimit = {
  min: number
  max: number
}

const PRESET_TOP_LEVEL_KEYS = ['kind', 'version', 'camera', 'effect'] as const
const CAMERA_KEYS = ['interpolation', 'easing', 'loopMode', 'currentFov', 'keyframes'] as const
const KEYFRAME_KEYS = ['time', 'position', 'target', 'fov'] as const
const EFFECT_KEYS = ['selectedEffectKey', 'adaptive', 'params'] as const
const BOX_KEYS = ['size', 'direction', 'duration', 'tintColor', 'edgeColor'] as const
const RADIAL_REVEAL_KEYS = [
  'duration',
  'dotTint',
  'waveTint',
  'intensity',
] as const
const RADIAL_REVEAL_MANUAL_KEYS = [
  'speed',
  'acceleration',
  'delay',
  'dotTint',
  'waveTint',
  'oscillationIntensity',
  'bandWidth',
] as const
const RAIN_REVEAL_KEYS = [
  'duration',
  'fallTint',
  'fallTintIntensity',
  'hitTint',
  'intensity',
] as const
const RAIN_REVEAL_MANUAL_KEYS = [
  'distance',
  'speed',
  'acceleration',
  'flightTime',
  'rainSize',
  'rotation',
  'fallTint',
  'fallTintIntensity',
  'hitTint',
  'hitDuration',
] as const
const GRID_REVEAL_KEYS = [
  'duration',
  'moveTint',
  'moveTintIntensity',
  'landTint',
  'intensity',
] as const
const GRID_REVEAL_MANUAL_KEYS = [
  'blockSize',
  'delay',
  'duration',
  'dotSize',
  'moveTint',
  'moveTintIntensity',
  'landTint',
  'landDuration',
] as const
const VISUAL_SHOWCASE_KEYS = [
  'intensity',
  'timeScale',
  'swayAmplitude',
  'swaySpeed',
  'flickerColor',
] as const
const REVEAL_SHOWCASE_KEYS = ['timeScale', 'motionScale', 'duration'] as const
const PARTICLE_SCATTER_KEYS = ['motion', 'style', 'jitter', 'timing', 'random'] as const
const PARTICLE_MOTION_KEYS = ['type', 'params'] as const
const PARTICLE_STYLE_KEYS = [
  'sizeMode',
  'targetSize',
  'targetSizeRatio',
  'opacity',
  'highlightStrength',
  'glowStrength',
  'glowRadius',
] as const
const PARTICLE_JITTER_KEYS = ['amplitude', 'speed'] as const
const PARTICLE_TIMING_KEYS = [
  'duration',
  'preScatterHoldDuration',
  'peakHoldDuration',
  'phaseWeights',
] as const
const PARTICLE_RANDOM_KEYS = ['seed'] as const
const CHAOTIC_PARTICLE_MOTION_KEYS = ['scatterScale'] as const
const LAYERED_PRINT_PARTICLE_MOTION_KEYS = [
  'layerCount',
  'bandWidth',
  'liftScale',
  'direction',
] as const
const FRACTAL_ORBIT_PARTICLE_MOTION_KEYS = ['orbitScale', 'frequency', 'sweepWidth'] as const
const PARTICLE_PHASE_WEIGHT_KEYS = ['particleize', 'scatter', 'gather', 'restore'] as const
const DISSOLVE_DRIFT_KEYS = ['speed', 'motionScale'] as const
const COLOR_CONTROL_KEYS = ['color', 'intensity'] as const

export function createGS3DEffectAnimationPreset(
  state: GS3DEffectAnimationPresetState,
): GS3DEffectAnimationPresetV1 {
  return normalizePresetObject({
    kind: GS3D_EFFECT_ANIMATION_PRESET_KIND,
    version: GS3D_EFFECT_ANIMATION_PRESET_VERSION,
    camera: {
      interpolation: state.camera.interpolation,
      easing: state.camera.interpolation === 'step' ? 'none' : state.camera.easing,
      loopMode: state.camera.loopMode,
      currentFov: state.camera.currentFov,
      keyframes: state.camera.keyframes.map((frame) => ({
        time: frame.time,
        position: [...frame.position],
        target: [...frame.target],
        fov: frame.fov,
      })),
    },
    effect: {
      selectedEffectKey: state.effect.selectedEffectKey,
      ...(isAdaptivePresetEffectKey(state.effect.selectedEffectKey) ? {
        adaptive: state.effect.adaptive ?? true,
      } : {}),
      params: createSelectedEffectParams(state.effect),
    },
  })
}

function createSelectedEffectParams(
  effect: GS3DEffectAnimationPresetState['effect'],
): unknown {
  switch (effect.selectedEffectKey) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
    case 'box-crop':
      return {
        size: effect.box.size,
        direction: effect.box.direction,
        duration: effect.box.duration,
        tintColor: effect.box.tintColor,
        edgeColor: effect.box.edgeColor,
      }
    case 'reveal-radial':
      if (effect.adaptive === false) {
        return {
          speed: effect.revealManual.radial.speed,
          acceleration: effect.revealManual.radial.acceleration,
          delay: effect.revealManual.radial.delay,
          dotTint: normalizePresetColorControl(effect.revealManual.radial.dotTint),
          waveTint: normalizePresetColorControl(effect.revealManual.radial.waveTint),
          oscillationIntensity: effect.revealManual.radial.oscillationIntensity,
          bandWidth: effect.revealManual.radial.bandWidth,
        }
      }
      return {
        duration: effect.reveal.radial.duration,
        dotTint: normalizePresetColorControl(effect.reveal.radial.dotTint),
        waveTint: normalizePresetColorControl(effect.reveal.radial.waveTint),
        intensity: effect.reveal.radial.intensity,
      }
    case 'reveal-rain':
      if (effect.adaptive === false) {
        return {
          distance: effect.revealManual.rain.distance,
          speed: effect.revealManual.rain.speed,
          acceleration: effect.revealManual.rain.acceleration,
          flightTime: effect.revealManual.rain.flightTime,
          rainSize: effect.revealManual.rain.rainSize,
          rotation: effect.revealManual.rain.rotation,
          fallTint: normalizePresetColorControl(effect.revealManual.rain.fallTint),
          fallTintIntensity: effect.revealManual.rain.fallTintIntensity,
          hitTint: normalizePresetColorControl(effect.revealManual.rain.hitTint),
          hitDuration: effect.revealManual.rain.hitDuration,
        }
      }
      return {
        duration: effect.reveal.rain.duration,
        fallTint: normalizePresetColorControl(effect.reveal.rain.fallTint),
        fallTintIntensity: effect.reveal.rain.fallTintIntensity,
        hitTint: normalizePresetColorControl(effect.reveal.rain.hitTint),
        intensity: effect.reveal.rain.intensity,
      }
    case 'reveal-grid':
      if (effect.adaptive === false) {
        return {
          blockSize: effect.revealManual.grid.blockSize,
          delay: effect.revealManual.grid.delay,
          duration: effect.revealManual.grid.duration,
          dotSize: effect.revealManual.grid.dotSize,
          moveTint: normalizePresetColorControl(effect.revealManual.grid.moveTint),
          moveTintIntensity: effect.revealManual.grid.moveTintIntensity,
          landTint: normalizePresetColorControl(effect.revealManual.grid.landTint),
          landDuration: effect.revealManual.grid.landDuration,
        }
      }
      return {
        duration: effect.reveal.grid.duration,
        moveTint: normalizePresetColorControl(effect.reveal.grid.moveTint),
        moveTintIntensity: effect.reveal.grid.moveTintIntensity,
        landTint: normalizePresetColorControl(effect.reveal.grid.landTint),
        intensity: effect.reveal.grid.intensity,
      }
    case 'obb-double-wave':
      if (effect.adaptive === false) {
        return {
          speed: effect.revealManual.obb.speed,
          acceleration: effect.revealManual.obb.acceleration,
          delay: effect.revealManual.obb.delay,
          dotTint: normalizePresetColorControl(effect.revealManual.obb.dotTint),
          waveTint: normalizePresetColorControl(effect.revealManual.obb.waveTint),
          oscillationIntensity: effect.revealManual.obb.oscillationIntensity,
          bandWidth: effect.revealManual.obb.bandWidth,
        }
      }
      return {
        duration: effect.reveal.obb.duration,
        dotTint: normalizePresetColorControl(effect.reveal.obb.dotTint),
        waveTint: normalizePresetColorControl(effect.reveal.obb.waveTint),
        intensity: effect.reveal.obb.intensity,
      }
    default:
      if (isVisualShowcaseEffectKey(effect.selectedEffectKey)) {
        const params = effect.visual[effect.selectedEffectKey]
        return {
          intensity: params.intensity,
          timeScale: params.timeScale,
          ...(effect.selectedEffectKey === 'visual-electronic' ? {
            swayAmplitude: params.swayAmplitude ?? 0.2,
            swaySpeed: params.swaySpeed ?? 2,
            flickerColor: normalizePresetColorControl(params.flickerColor ?? { hex: '#0080b3', intensity: 1 }),
          } : {}),
        }
      }
      if (isRevealShowcaseEffectKey(effect.selectedEffectKey)) {
        const params = effect.showcaseReveal[effect.selectedEffectKey]
        return {
          timeScale: params.timeScale,
          motionScale: params.motionScale,
          duration: params.duration,
        }
      }
      if (isParticleEffectKey(effect.selectedEffectKey)) {
        const params = effect.particle[effect.selectedEffectKey]
        return cloneParticleScatterParams(params)
      }
      if (isDissolveEffectKey(effect.selectedEffectKey)) {
        const params = effect.dissolve[effect.selectedEffectKey]
        return {
          speed: params.speed,
          motionScale: params.motionScale,
        }
      }
      return {}
  }
}

function isAdaptivePresetEffectKey(value: string): boolean {
  return (
    value === 'reveal-radial' ||
    value === 'reveal-rain' ||
    value === 'reveal-grid' ||
    value === 'obb-double-wave'
  )
}

export function stringifyGS3DEffectAnimationPreset(
  preset: GS3DEffectAnimationPresetV1,
): string {
  return `${JSON.stringify(preset, null, 2)}\n`
}

export function parseGS3DEffectAnimationPreset(
  source: string | unknown,
): GS3DEffectAnimationPresetV1 {
  const value = typeof source === 'string' ? parseJson(source) : source
  return normalizePresetObject(value)
}

export function applyGS3DEffectAnimationPresetImport(
  preset: GS3DEffectAnimationPresetV1,
  operations: GS3DEffectAnimationPresetApplyOperations,
): void {
  operations.stopPlayback()
  operations.clearActiveEffect()
  operations.writeState(preset)

  const firstFrame = preset.camera.keyframes[0]
  if (firstFrame) {
    operations.applyFirstKeyframe(firstFrame)
    return
  }

  operations.applyCurrentFov(preset.camera.currentFov)
}

export function parseAndApplyGS3DEffectAnimationPresetImport(
  source: string | unknown,
  operations: GS3DEffectAnimationPresetApplyOperations,
): GS3DEffectAnimationPresetV1 {
  const preset = parseGS3DEffectAnimationPreset(source)
  applyGS3DEffectAnimationPresetImport(preset, operations)
  return preset
}

export function normalizePresetColorControl(
  control: GS3DEffectAnimationPresetColorControlInput,
): GS3DEffectAnimationPresetColorControl {
  return {
    color: normalizeColorHex(control.hex, 'color'),
    intensity: clampFiniteNumber(
      control.intensity,
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.colorIntensity,
      'intensity',
    ),
  }
}

export function presetColorControlToColorTuple(
  control: GS3DEffectAnimationPresetColorControl,
): GS3DEffectAnimationPresetColorTuple {
  const color = parseColorTuple(control.color, 'color')
  const intensity = clampFiniteNumber(
    control.intensity,
    GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.colorIntensity,
    'intensity',
  )
  return [color[0] * intensity, color[1] * intensity, color[2] * intensity]
}

function isVisualShowcaseEffectKey(value: string): value is GS3DEffectAnimationPresetVisualShowcaseKey {
  return GS3D_EFFECT_ANIMATION_PRESET_VISUAL_SHOWCASE_KEYS.includes(
    value as GS3DEffectAnimationPresetVisualShowcaseKey,
  )
}

function isRevealShowcaseEffectKey(value: string): value is GS3DEffectAnimationPresetRevealShowcaseKey {
  return GS3D_EFFECT_ANIMATION_PRESET_REVEAL_SHOWCASE_KEYS.includes(
    value as GS3DEffectAnimationPresetRevealShowcaseKey,
  )
}

function isDissolveEffectKey(value: string): value is GS3DEffectAnimationPresetDissolveKey {
  return GS3D_EFFECT_ANIMATION_PRESET_DISSOLVE_KEYS.includes(
    value as GS3DEffectAnimationPresetDissolveKey,
  )
}

function isParticleEffectKey(value: string): value is GS3DEffectAnimationPresetParticleKey {
  return GS3D_EFFECT_ANIMATION_PRESET_PARTICLE_KEYS.includes(
    value as GS3DEffectAnimationPresetParticleKey,
  )
}

function parseJson(source: string): unknown {
  try {
    return JSON.parse(source)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`preset JSON 解析失败：${message}`)
  }
}

function normalizePresetObject(value: unknown): GS3DEffectAnimationPresetV1 {
  const preset = requireRecord(value, 'preset')
  assertNoUnknownKeys(preset, PRESET_TOP_LEVEL_KEYS, 'preset')

  if (preset.kind !== GS3D_EFFECT_ANIMATION_PRESET_KIND) {
    throw new Error('preset.kind 不是 gs3d-effect-animation 示例预设')
  }
  if (preset.version !== GS3D_EFFECT_ANIMATION_PRESET_VERSION) {
    throw new Error('preset.version 只支持版本 1')
  }

  return {
    kind: GS3D_EFFECT_ANIMATION_PRESET_KIND,
    version: GS3D_EFFECT_ANIMATION_PRESET_VERSION,
    camera: parseCamera(requireRecord(preset.camera, 'camera')),
    effect: parseEffect(requireRecord(preset.effect, 'effect')),
  }
}

function parseCamera(camera: Record<string, unknown>): GS3DEffectAnimationPresetCamera {
  assertNoUnknownKeys(camera, CAMERA_KEYS, 'camera')

  const interpolation = readEnum(
    camera,
    'interpolation',
    GS3D_EFFECT_ANIMATION_PRESET_INTERPOLATIONS,
    'camera.interpolation',
  )
  const easing = readEnum(
    camera,
    'easing',
    GS3D_EFFECT_ANIMATION_PRESET_EASINGS,
    'camera.easing',
  )
  if (interpolation === 'step' && easing !== 'none') {
    throw new Error('camera.easing 在 step 插值方式下只能为 none')
  }

  return {
    interpolation,
    easing,
    loopMode: readEnum(
      camera,
      'loopMode',
      GS3D_EFFECT_ANIMATION_PRESET_LOOP_MODES,
      'camera.loopMode',
    ),
    currentFov: readNumber(
      camera,
      'currentFov',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.cameraFov,
      'camera.currentFov',
    ),
    keyframes: parseCameraKeyframes(camera.keyframes),
  }
}

function parseCameraKeyframes(value: unknown): GS3DEffectAnimationPresetCameraKeyframe[] {
  if (!Array.isArray(value)) {
    throw new Error('camera.keyframes 必须是数组')
  }

  let previousTime = -Number.EPSILON
  return value.map((item, index) => {
    const path = `camera.keyframes[${index}]`
    const frame = requireRecord(item, path)
    assertNoUnknownKeys(frame, KEYFRAME_KEYS, path)
    const time = readNumber(frame, 'time', { min: 0, max: Number.MAX_SAFE_INTEGER }, `${path}.time`)
    if (time <= previousTime) {
      throw new Error(`${path}.time 必须严格递增`)
    }
    previousTime = time

    return {
      time,
      position: readVector3(frame.position, `${path}.position`),
      target: readVector3(frame.target, `${path}.target`),
      fov: readNumber(
        frame,
        'fov',
        GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.cameraFov,
        `${path}.fov`,
      ),
    }
  })
}

function parseEffect(effect: Record<string, unknown>): GS3DEffectAnimationPresetEffect {
  assertNoUnknownKeys(effect, EFFECT_KEYS, 'effect')

  const selectedEffectKey = readEnum(
    effect,
    'selectedEffectKey',
    GS3D_EFFECT_ANIMATION_PRESET_EFFECT_KEYS,
    'effect.selectedEffectKey',
  )
  const params = requireRecord(effect.params, 'effect.params')

  switch (selectedEffectKey) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
    case 'box-crop':
      assertNoUnsupportedAdaptive(effect.adaptive, selectedEffectKey)
      return {
        selectedEffectKey,
        params: parseBox(params, 'effect.params'),
      }
    case 'reveal-radial': {
      const radialAdaptive = readOptionalAdaptive(effect.adaptive, selectedEffectKey)
      if (radialAdaptive) {
        return {
          selectedEffectKey,
          adaptive: true,
          params: parseRadialReveal(params, 'radial', 'effect.params'),
        }
      }
      return {
        selectedEffectKey,
        adaptive: false,
        params: parseRadialRevealManual(params, 'radial', 'effect.params'),
      }
    }
    case 'reveal-rain': {
      const rainAdaptive = readOptionalAdaptive(effect.adaptive, selectedEffectKey)
      if (rainAdaptive) {
        return {
          selectedEffectKey,
          adaptive: true,
          params: parseRainReveal(params, 'effect.params'),
        }
      }
      return {
        selectedEffectKey,
        adaptive: false,
        params: parseRainRevealManual(params, 'effect.params'),
      }
    }
    case 'reveal-grid': {
      const gridAdaptive = readOptionalAdaptive(effect.adaptive, selectedEffectKey)
      if (gridAdaptive) {
        return {
          selectedEffectKey,
          adaptive: true,
          params: parseGridReveal(params, 'effect.params'),
        }
      }
      return {
        selectedEffectKey,
        adaptive: false,
        params: parseGridRevealManual(params, 'effect.params'),
      }
    }
    case 'obb-double-wave': {
      const obbAdaptive = readOptionalAdaptive(effect.adaptive, selectedEffectKey)
      if (obbAdaptive) {
        return {
          selectedEffectKey,
          adaptive: true,
          params: parseRadialReveal(params, 'obb', 'effect.params'),
        }
      }
      return {
        selectedEffectKey,
        adaptive: false,
        params: parseRadialRevealManual(params, 'obb', 'effect.params'),
      }
    }
    case 'visual-electronic':
    case 'visual-deep-meditation':
    case 'visual-waves':
    case 'visual-flare':
    case 'visual-disintegrate':
      assertNoUnsupportedAdaptive(effect.adaptive, selectedEffectKey)
      return {
        selectedEffectKey,
        params: parseVisualShowcase(params, 'effect.params'),
      }
    case 'reveal-magic':
    case 'reveal-spread':
    case 'reveal-unroll':
    case 'reveal-twister':
      assertNoUnsupportedAdaptive(effect.adaptive, selectedEffectKey)
      return {
        selectedEffectKey,
        params: parseRevealShowcase(params, 'effect.params'),
      }
    case 'particle-scatter-reassemble':
      assertNoUnsupportedAdaptive(effect.adaptive, selectedEffectKey)
      return {
        selectedEffectKey,
        params: parseParticleScatter(params, 'effect.params'),
      }
    case 'dissolve-drift':
      assertNoUnsupportedAdaptive(effect.adaptive, selectedEffectKey)
      return {
        selectedEffectKey,
        params: parseDissolveDrift(params, 'effect.params'),
      }
  }
}

function assertNoUnsupportedAdaptive(value: unknown, effectKey: string): void {
  if (value !== undefined) {
    throw new Error(`effect.adaptive 不支持用于 ${effectKey}`)
  }
}

function readOptionalAdaptive(value: unknown, effectKey: string): boolean {
  if (value === undefined) {
    return true
  }
  if (!isAdaptivePresetEffectKey(effectKey)) {
    throw new Error(`effect.adaptive 不支持用于 ${effectKey}`)
  }
  if (typeof value !== 'boolean') {
    throw new Error('effect.adaptive 必须是 boolean')
  }
  return value
}

function parseBox(box: Record<string, unknown>, path: string): GS3DEffectAnimationPresetBox {
  assertNoUnknownKeys(box, BOX_KEYS, path)

  return {
    size: readNumber(
      box,
      'size',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.boxSize,
      `${path}.size`,
    ),
    direction: readEnum(
      box,
      'direction',
      GS3D_EFFECT_ANIMATION_PRESET_BOX_DIRECTIONS,
      `${path}.direction`,
    ),
    duration: readNumber(
      box,
      'duration',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.boxDuration,
      `${path}.duration`,
    ),
    tintColor: readColorHex(box, 'tintColor', `${path}.tintColor`),
    edgeColor: readColorHex(box, 'edgeColor', `${path}.edgeColor`),
  }
}

function parseRadialReveal(
  reveal: Record<string, unknown>,
  key: 'radial' | 'obb',
  path: string,
): GS3DEffectAnimationPresetRadialReveal {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS[key]
  assertNoUnknownKeys(reveal, RADIAL_REVEAL_KEYS, path)

  return {
    duration: readNumber(
      reveal,
      'duration',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.revealDuration,
      `${path}.duration`,
    ),
    dotTint: parseColorControl(requireRecord(reveal.dotTint, `${path}.dotTint`), `${path}.dotTint`),
    waveTint: parseColorControl(requireRecord(reveal.waveTint, `${path}.waveTint`), `${path}.waveTint`),
    intensity: readNumber(reveal, 'intensity', limits.intensity, `${path}.intensity`),
  }
}

function parseRadialRevealManual(
  reveal: Record<string, unknown>,
  key: 'radial' | 'obb',
  path: string,
): GS3DEffectAnimationPresetRadialRevealManual {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS[key]
  assertNoUnknownKeys(reveal, RADIAL_REVEAL_MANUAL_KEYS, path)

  return {
    speed: readNumber(reveal, 'speed', limits.speed, `${path}.speed`),
    acceleration: readNumber(
      reveal,
      'acceleration',
      limits.acceleration,
      `${path}.acceleration`,
    ),
    delay: readNumber(reveal, 'delay', limits.delay, `${path}.delay`),
    dotTint: parseColorControl(requireRecord(reveal.dotTint, `${path}.dotTint`), `${path}.dotTint`),
    waveTint: parseColorControl(requireRecord(reveal.waveTint, `${path}.waveTint`), `${path}.waveTint`),
    oscillationIntensity: readNumber(
      reveal,
      'oscillationIntensity',
      limits.oscillationIntensity,
      `${path}.oscillationIntensity`,
    ),
    bandWidth: readNumber(reveal, 'bandWidth', limits.bandWidth, `${path}.bandWidth`),
  }
}

function parseRainReveal(
  reveal: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetRainReveal {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.rain
  assertNoUnknownKeys(reveal, RAIN_REVEAL_KEYS, path)

  return {
    duration: readNumber(
      reveal,
      'duration',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.revealDuration,
      `${path}.duration`,
    ),
    fallTint: parseColorControl(requireRecord(reveal.fallTint, `${path}.fallTint`), `${path}.fallTint`),
    fallTintIntensity: readNumber(
      reveal,
      'fallTintIntensity',
      limits.fallTintIntensity,
      `${path}.fallTintIntensity`,
    ),
    hitTint: parseColorControl(requireRecord(reveal.hitTint, `${path}.hitTint`), `${path}.hitTint`),
    intensity: readNumber(reveal, 'intensity', limits.intensity, `${path}.intensity`),
  }
}

function parseRainRevealManual(
  reveal: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetRainRevealManual {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.rain
  assertNoUnknownKeys(reveal, RAIN_REVEAL_MANUAL_KEYS, path)

  return {
    distance: readNumber(reveal, 'distance', limits.distance, `${path}.distance`),
    speed: readNumber(reveal, 'speed', limits.speed, `${path}.speed`),
    acceleration: readNumber(
      reveal,
      'acceleration',
      limits.acceleration,
      `${path}.acceleration`,
    ),
    flightTime: readNumber(reveal, 'flightTime', limits.flightTime, `${path}.flightTime`),
    rainSize: readNumber(reveal, 'rainSize', limits.rainSize, `${path}.rainSize`),
    rotation: readNumber(reveal, 'rotation', limits.rotation, `${path}.rotation`),
    fallTint: parseColorControl(requireRecord(reveal.fallTint, `${path}.fallTint`), `${path}.fallTint`),
    fallTintIntensity: readNumber(
      reveal,
      'fallTintIntensity',
      limits.fallTintIntensity,
      `${path}.fallTintIntensity`,
    ),
    hitTint: parseColorControl(requireRecord(reveal.hitTint, `${path}.hitTint`), `${path}.hitTint`),
    hitDuration: readNumber(reveal, 'hitDuration', limits.hitDuration, `${path}.hitDuration`),
  }
}

function parseGridReveal(
  reveal: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetGridReveal {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.grid
  assertNoUnknownKeys(reveal, GRID_REVEAL_KEYS, path)

  return {
    duration: readNumber(
      reveal,
      'duration',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.revealDuration,
      `${path}.duration`,
    ),
    moveTint: parseColorControl(requireRecord(reveal.moveTint, `${path}.moveTint`), `${path}.moveTint`),
    moveTintIntensity: readNumber(
      reveal,
      'moveTintIntensity',
      limits.moveTintIntensity,
      `${path}.moveTintIntensity`,
    ),
    landTint: parseColorControl(requireRecord(reveal.landTint, `${path}.landTint`), `${path}.landTint`),
    intensity: readNumber(reveal, 'intensity', limits.intensity, `${path}.intensity`),
  }
}

function parseGridRevealManual(
  reveal: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetGridRevealManual {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.grid
  assertNoUnknownKeys(reveal, GRID_REVEAL_MANUAL_KEYS, path)

  return {
    blockSize: readNumber(reveal, 'blockSize', limits.blockSize, `${path}.blockSize`),
    delay: readNumber(reveal, 'delay', limits.delay, `${path}.delay`),
    duration: readNumber(reveal, 'duration', limits.duration, `${path}.duration`),
    dotSize: readNumber(reveal, 'dotSize', limits.dotSize, `${path}.dotSize`),
    moveTint: parseColorControl(requireRecord(reveal.moveTint, `${path}.moveTint`), `${path}.moveTint`),
    moveTintIntensity: readNumber(
      reveal,
      'moveTintIntensity',
      limits.moveTintIntensity,
      `${path}.moveTintIntensity`,
    ),
    landTint: parseColorControl(requireRecord(reveal.landTint, `${path}.landTint`), `${path}.landTint`),
    landDuration: readNumber(reveal, 'landDuration', limits.landDuration, `${path}.landDuration`),
  }
}

function parseVisualShowcase(
  params: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetVisualShowcase {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.visualShowcase
  assertNoUnknownKeys(params, VISUAL_SHOWCASE_KEYS, path)

  return {
    intensity: readNumber(params, 'intensity', limits.intensity, `${path}.intensity`),
    timeScale: readNumber(params, 'timeScale', limits.timeScale, `${path}.timeScale`),
    swayAmplitude: params.swayAmplitude === undefined ?
      undefined :
      readNumber(params, 'swayAmplitude', limits.swayAmplitude, `${path}.swayAmplitude`),
    swaySpeed: params.swaySpeed === undefined ?
      undefined :
      readNumber(params, 'swaySpeed', limits.swaySpeed, `${path}.swaySpeed`),
    flickerColor: params.flickerColor === undefined ?
      undefined :
      parseColorControl(requireRecord(params.flickerColor, `${path}.flickerColor`), `${path}.flickerColor`),
  }
}

function parseRevealShowcase(
  params: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetRevealShowcase {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.revealShowcase
  assertNoUnknownKeys(params, REVEAL_SHOWCASE_KEYS, path)

  return {
    timeScale: readNumber(params, 'timeScale', limits.timeScale, `${path}.timeScale`),
    motionScale: readNumber(params, 'motionScale', limits.motionScale, `${path}.motionScale`),
    duration: readNumber(params, 'duration', limits.duration, `${path}.duration`),
  }
}

function parseParticleScatter(
  params: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetParticleScatter {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter
  assertNoUnknownKeys(params, PARTICLE_SCATTER_KEYS, path)

  const style = requireRecord(params.style, `${path}.style`)
  assertNoUnknownKeys(style, PARTICLE_STYLE_KEYS, `${path}.style`)
  const jitter = requireRecord(params.jitter, `${path}.jitter`)
  assertNoUnknownKeys(jitter, PARTICLE_JITTER_KEYS, `${path}.jitter`)
  const timing = requireRecord(params.timing, `${path}.timing`)
  assertNoUnknownKeys(timing, PARTICLE_TIMING_KEYS, `${path}.timing`)
  const random = requireRecord(params.random, `${path}.random`)
  assertNoUnknownKeys(random, PARTICLE_RANDOM_KEYS, `${path}.random`)

  const duration = readNumber(timing, 'duration', limits.duration, `${path}.timing.duration`)
  const peakHoldDuration = readNumber(
    timing,
    'peakHoldDuration',
    limits.peakHoldDuration,
    `${path}.timing.peakHoldDuration`,
  )
  const preScatterHoldDuration = readOptionalNumber(
    timing,
    'preScatterHoldDuration',
    limits.preScatterHoldDuration,
    `${path}.timing.preScatterHoldDuration`,
    GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS.timing.preScatterHoldDuration,
  )
  if (preScatterHoldDuration + peakHoldDuration >= duration) {
    throw new Error(
      `${path}.timing.preScatterHoldDuration 与 peakHoldDuration 之和必须小于 duration`,
    )
  }

  const phaseWeightValues = requireRecord(
    timing.phaseWeights,
    `${path}.timing.phaseWeights`,
  )
  assertNoUnknownKeys(
    phaseWeightValues,
    PARTICLE_PHASE_WEIGHT_KEYS,
    `${path}.timing.phaseWeights`,
  )
  const normalizedPhaseWeights = {
    particleize: readNumber(
      phaseWeightValues,
      'particleize',
      limits.phaseWeight,
      `${path}.timing.phaseWeights.particleize`,
    ),
    scatter: readNumber(
      phaseWeightValues,
      'scatter',
      limits.phaseWeight,
      `${path}.timing.phaseWeights.scatter`,
    ),
    gather: readNumber(
      phaseWeightValues,
      'gather',
      limits.phaseWeight,
      `${path}.timing.phaseWeights.gather`,
    ),
    restore: readNumber(
      phaseWeightValues,
      'restore',
      limits.phaseWeight,
      `${path}.timing.phaseWeights.restore`,
    ),
  }
  if (
    normalizedPhaseWeights.particleize +
      normalizedPhaseWeights.scatter +
      normalizedPhaseWeights.gather +
      normalizedPhaseWeights.restore <=
    0
  ) {
    throw new Error(`${path}.timing.phaseWeights 至少需要一个大于 0 的阶段权重`)
  }

  return {
    motion: parseParticleMotion(params.motion, `${path}.motion`),
    style: {
      sizeMode: readOptionalEnum(
        style,
        'sizeMode',
        GS3D_EFFECT_ANIMATION_PARTICLE_SIZE_MODES,
        `${path}.style.sizeMode`,
        GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS.style.sizeMode,
      ),
      targetSize: readNumber(
        style,
        'targetSize',
        limits.particleTargetSize,
        `${path}.style.targetSize`,
      ),
      targetSizeRatio: readOptionalNumber(
        style,
        'targetSizeRatio',
        limits.particleTargetSizeRatio,
        `${path}.style.targetSizeRatio`,
        GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS.style.targetSizeRatio,
      ),
      opacity: readNumber(
        style,
        'opacity',
        limits.particleOpacity,
        `${path}.style.opacity`,
      ),
      highlightStrength: readNumber(
        style,
        'highlightStrength',
        limits.particleHighlightStrength,
        `${path}.style.highlightStrength`,
      ),
      glowStrength: readNumber(
        style,
        'glowStrength',
        limits.particleGlowStrength,
        `${path}.style.glowStrength`,
      ),
      glowRadius: readNumber(
        style,
        'glowRadius',
        limits.particleGlowRadius,
        `${path}.style.glowRadius`,
      ),
    },
    jitter: {
      amplitude: readOptionalNumber(
        jitter,
        'amplitude',
        limits.particleJitterAmplitude,
        `${path}.jitter.amplitude`,
        GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS.jitter.amplitude,
      ),
      speed: readOptionalNumber(
        jitter,
        'speed',
        limits.particleJitterSpeed,
        `${path}.jitter.speed`,
        GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS.jitter.speed,
      ),
    },
    timing: {
      duration,
      preScatterHoldDuration,
      peakHoldDuration,
      phaseWeights: normalizedPhaseWeights,
    },
    random: {
      seed: readInteger(random, 'seed', limits.seed, `${path}.random.seed`),
    },
  }
}

function parseParticleMotion(value: unknown, path: string): GSplatParticleMotion {
  if (value === undefined) {
    return {
      type: 'chaotic',
      params: { ...GSPLAT_PARTICLE_MOTION_DEFAULTS.chaotic },
    }
  }

  const motion = requireRecord(value, path)
  assertNoUnknownKeys(motion, PARTICLE_MOTION_KEYS, path)
  const type = readOptionalEnum(
    motion,
    'type',
    GSPLAT_PARTICLE_MOTION_TYPES,
    `${path}.type`,
    'chaotic',
  )
  const params = motion.params === undefined ? {} : requireRecord(motion.params, `${path}.params`)
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter

  switch (type) {
    case 'chaotic':
      assertNoUnknownKeys(params, CHAOTIC_PARTICLE_MOTION_KEYS, `${path}.params`)
      return {
        type,
        params: {
          scatterScale: readOptionalNumber(
            params,
            'scatterScale',
            limits.scatterScale,
            `${path}.params.scatterScale`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS.chaotic.scatterScale,
          ),
        },
      }
    case 'layered-print':
      assertNoUnknownKeys(params, LAYERED_PRINT_PARTICLE_MOTION_KEYS, `${path}.params`)
      return {
        type,
        params: {
          layerCount: readOptionalInteger(
            params,
            'layerCount',
            limits.layerCount,
            `${path}.params.layerCount`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].layerCount,
          ),
          bandWidth: readOptionalNumber(
            params,
            'bandWidth',
            limits.printBandWidth,
            `${path}.params.bandWidth`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].bandWidth,
          ),
          liftScale: readOptionalNumber(
            params,
            'liftScale',
            limits.liftScale,
            `${path}.params.liftScale`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].liftScale,
          ),
          direction: readOptionalEnum(
            params,
            'direction',
            GSPLAT_LAYERED_PRINT_DIRECTIONS,
            `${path}.params.direction`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'].direction,
          ),
        },
      }
    case 'fractal-orbit':
      assertNoUnknownKeys(params, FRACTAL_ORBIT_PARTICLE_MOTION_KEYS, `${path}.params`)
      return {
        type,
        params: {
          orbitScale: readOptionalNumber(
            params,
            'orbitScale',
            limits.orbitScale,
            `${path}.params.orbitScale`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'].orbitScale,
          ),
          frequency: readOptionalNumber(
            params,
            'frequency',
            limits.orbitFrequency,
            `${path}.params.frequency`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'].frequency,
          ),
          sweepWidth: readOptionalNumber(
            params,
            'sweepWidth',
            limits.orbitSweepWidth,
            `${path}.params.sweepWidth`,
            GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'].sweepWidth,
          ),
        },
      }
  }
}

export function cloneParticleMotion(motion: GSplatParticleMotion): GSplatParticleMotion {
  switch (motion.type) {
    case 'chaotic':
      return { type: motion.type, params: { ...motion.params } }
    case 'layered-print':
      return { type: motion.type, params: { ...motion.params } }
    case 'fractal-orbit':
      return { type: motion.type, params: { ...motion.params } }
  }
}

export function cloneParticleScatterParams(
  params: GS3DEffectAnimationPresetParticleScatter,
): GS3DEffectAnimationPresetParticleScatter {
  return {
    motion: cloneParticleMotion(params.motion),
    style: { ...params.style },
    jitter: { ...params.jitter },
    timing: {
      ...params.timing,
      phaseWeights: { ...params.timing.phaseWeights },
    },
    random: { ...params.random },
  }
}

function parseDissolveDrift(
  params: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetDissolveDrift {
  const limits = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.dissolve
  assertNoUnknownKeys(params, DISSOLVE_DRIFT_KEYS, path)

  return {
    speed: readNumber(params, 'speed', limits.speed, `${path}.speed`),
    motionScale: readNumber(params, 'motionScale', limits.motionScale, `${path}.motionScale`),
  }
}

function parseColorControl(
  control: Record<string, unknown>,
  path: string,
): GS3DEffectAnimationPresetColorControl {
  assertNoUnknownKeys(control, COLOR_CONTROL_KEYS, path)

  return {
    color: readColorHex(control, 'color', `${path}.color`),
    intensity: readNumber(
      control,
      'intensity',
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.colorIntensity,
      `${path}.intensity`,
    ),
  }
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${path} 必须是对象`)
  }

  return value as Record<string, unknown>
}

function assertNoUnknownKeys(
  record: Record<string, unknown>,
  allowedKeys: readonly string[],
  path: string,
): void {
  const allowed = new Set(allowedKeys)
  const unknownKeys = Object.keys(record).filter((key) => !allowed.has(key))
  if (unknownKeys.length > 0) {
    throw new Error(`${path} 包含未支持字段：${unknownKeys.join(', ')}`)
  }
}

function readEnum<T extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: T,
  path: string,
): T[number] {
  const value = record[key]
  if (typeof value === 'string' && values.includes(value)) {
    return value
  }

  throw new Error(`${path} 不是支持的取值`)
}

function readOptionalEnum<T extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: T,
  path: string,
  fallback: T[number],
): T[number] {
  return record[key] === undefined ? fallback : readEnum(record, key, values, path)
}

function readNumber(
  record: Record<string, unknown>,
  key: string,
  limit: NumericLimit,
  path: string,
): number {
  return clampFiniteNumber(record[key], limit, path)
}

function readOptionalNumber(
  record: Record<string, unknown>,
  key: string,
  limit: NumericLimit,
  path: string,
  fallback: number,
): number {
  return record[key] === undefined ? fallback : readNumber(record, key, limit, path)
}

function readInteger(
  record: Record<string, unknown>,
  key: string,
  limit: NumericLimit,
  path: string,
): number {
  const value = record[key]
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new Error(`${path} 必须是整数`)
  }
  return clampFiniteNumber(value, limit, path)
}

function readOptionalInteger(
  record: Record<string, unknown>,
  key: string,
  limit: NumericLimit,
  path: string,
  fallback: number,
): number {
  return record[key] === undefined ? fallback : readInteger(record, key, limit, path)
}

function clampFiniteNumber(value: unknown, limit: NumericLimit, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${path} 必须是有限数字`)
  }

  return Math.min(limit.max, Math.max(limit.min, value))
}

function readVector3(value: unknown, path: string): GS3DEffectAnimationPresetVector3 {
  if (!Array.isArray(value) || value.length !== 3) {
    throw new Error(`${path} 必须是长度为 3 的数字数组`)
  }

  return value.map((item, index) => {
    if (typeof item !== 'number' || !Number.isFinite(item)) {
      throw new Error(`${path}[${index}] 必须是有限数字`)
    }
    return item
  }) as GS3DEffectAnimationPresetVector3
}

function readColorHex(record: Record<string, unknown>, key: string, path: string): string {
  const value = record[key]
  if (typeof value !== 'string') {
    throw new Error(`${path} 必须是 hex 颜色字符串`)
  }

  return normalizeColorHex(value, path)
}

function normalizeColorHex(value: string, path: string): string {
  const hex = /^#?([0-9a-f]{6})$/i.exec(value.trim())?.[1]
  if (!hex) {
    throw new Error(`${path} 必须是 6 位 hex 颜色`)
  }

  return `#${hex.toLowerCase()}`
}

function parseColorTuple(value: string, path: string): GS3DEffectAnimationPresetColorTuple {
  const hex = normalizeColorHex(value, path).slice(1)
  return [
    Number.parseInt(hex.slice(0, 2), 16) / 255,
    Number.parseInt(hex.slice(2, 4), 16) / 255,
    Number.parseInt(hex.slice(4, 6), 16) / 255,
  ]
}
